import { Fragment, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import LinearProgress from '@mui/material/LinearProgress';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import FileDownloadIcon from '@mui/icons-material/FileDownloadOutlined';
import { useFilteredRows } from '../hooks/useFilteredRows';
import { useData } from '../store/data';
import { useFilters } from '../store/filters';
import { countBy, sortedByCount } from '../lib/filter-rows';
import { isDone } from '../lib/status';
import { fmtInt } from '../lib/format';
import {
  computeChecklistStats,
  CHECKLIST_LABELS, CHECKLIST_COLORS,
  type ChecklistBucket,
} from '../lib/checklist';
import { exportChecklistXlsx } from '../lib/checklist-export';
import type { Issue } from '../types/issue';

const JIRA_URL_KEY = 'v2.jira.baseUrl';
function loadJiraBaseUrl(): string {
  if (typeof window === 'undefined') return 'https://schueco.atlassian.net';
  return (localStorage.getItem(JIRA_URL_KEY) || 'https://schueco.atlassian.net').replace(/\/+$/, '');
}

type StatusScope = 'open' | 'closed' | 'all';

export function ChecklistView() {
  const rowsFull   = useFilteredRows() as Issue[];
  const allRows    = useData(s => s.rows) as Issue[];
  const teamFilter = useFilters(s => s.team);
  const setFilters = useFilters(s => s.set);
  const jiraBase   = useMemo(() => loadJiraBaseUrl(), []);
  const [scope, setScope] = useState<StatusScope>('open');

  const allTeams = useMemo(
    () => sortedByCount(countBy(allRows, 'Team Name')),
    [allRows]
  );

  const rows = useMemo(() => {
    if (scope === 'all') return rowsFull;
    return rowsFull.filter(r => {
      const done = isDone(String(r['Status'] ?? ''));
      return scope === 'open' ? !done : done;
    });
  }, [rowsFull, scope]);

  const stats = useMemo(() => computeChecklistStats(rows), [rows]);
  const coveragePct = stats.total > 0 ? (stats.withText     / stats.total) * 100 : 0;
  const acPct       = stats.total > 0 ? (stats.withACHeader / stats.total) * 100 : 0;
  const doneItemsPct = (stats.sumItemsDone + stats.sumItemsOpen) > 0
    ? (stats.sumItemsDone / (stats.sumItemsDone + stats.sumItemsOpen)) * 100 : 0;

  const bucketOrder: ChecklistBucket[] = ['empty', 'header_only', 'no_items', 'all_open', 'partial', 'all_done'];

  return (
    <Stack spacing={2}>
      {/* Team-Chip-Reihe */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', p: 1, bgcolor: 'action.hover', borderRadius: 2 }}>
        <Typography variant="caption" sx={{ fontWeight: 700, mr: 0.5, color: 'text.secondary' }}>TEAM</Typography>
        <Chip size="small" label="Alle"
          color={teamFilter === '' ? 'primary' : 'default'}
          variant={teamFilter === '' ? 'filled' : 'outlined'}
          onClick={() => setFilters({ team: '' })} clickable />
        {allTeams.map(([name, count]) => (
          <Chip key={name} size="small" label={`${name} (${count})`}
            color={teamFilter === name ? 'primary' : 'default'}
            variant={teamFilter === name ? 'filled' : 'outlined'}
            onClick={() => setFilters({ team: name })} clickable />
        ))}
      </Box>

      {/* Status-Scope + Excel-Export */}
      <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
        <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>STATUS</Typography>
        <ToggleButtonGroup size="small" value={scope} exclusive onChange={(_, v) => v && setScope(v as StatusScope)}>
          <ToggleButton value="open">nur offen</ToggleButton>
          <ToggleButton value="closed">nur geschlossen</ToggleButton>
          <ToggleButton value="all">alle</ToggleButton>
        </ToggleButtonGroup>
        <Typography variant="caption" color="text.secondary">
          Geschlossen = Status Closed / Resolved / Aborted
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Button
          size="small"
          variant="outlined"
          startIcon={<FileDownloadIcon />}
          disabled={rows.length === 0}
          onClick={() => exportChecklistXlsx(rows, {
            jiraBaseUrl: jiraBase,
            team: teamFilter,
            scope,
          })}
        >
          Excel-Export ({fmtInt(rows.length)})
        </Button>
      </Stack>

      {/* KPI-Zeile */}
      <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
        <Kpi title="Σ Tickets" value={fmtInt(stats.total)} />
        <Kpi
          title="mit Checklist-Text"
          value={fmtInt(stats.withText)}
          subtitle={`${coveragePct.toFixed(1)}% Abdeckung`}
          accent="#2e7d32"
          progress={coveragePct}
        />
        <Kpi
          title="mit AC-Header"
          value={fmtInt(stats.withACHeader)}
          subtitle={`${acPct.toFixed(1)}% erkennen „Acceptance Criteria"`}
          accent="#1976d2"
          progress={acPct}
        />
        <Kpi
          title="Erledigte Items"
          value={fmtInt(stats.sumItemsDone)}
          subtitle={`${doneItemsPct.toFixed(1)}% aller ${fmtInt(stats.sumItemsDone + stats.sumItemsOpen)} Items`}
          accent="#f57c00"
        />
      </Stack>

      {/* Qualitäts-Buckets */}
      <Card variant="outlined">
        <CardContent sx={{ py: 1.5 }}>
          <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            Qualitäts-Buckets
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Verteilung aller Tickets im aktuellen Filter — von „leer" bis „alle Items erledigt".
          </Typography>
          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
            {bucketOrder.map(b => {
              const n = stats.bucketCounts[b];
              const pct = stats.total > 0 ? (n / stats.total) * 100 : 0;
              return (
                <Chip
                  key={b}
                  size="small"
                  label={`${CHECKLIST_LABELS[b]} · ${fmtInt(n)} (${pct.toFixed(1)}%)`}
                  sx={{
                    bgcolor: CHECKLIST_COLORS[b],
                    color: '#fff',
                    fontWeight: 700,
                  }}
                />
              );
            })}
          </Stack>
        </CardContent>
      </Card>

      {/* Abdeckung pro Typ + Team */}
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
        <Card variant="outlined" sx={{ flex: 1 }}>
          <CardContent sx={{ py: 1.5 }}>
            <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              Abdeckung pro Issue-Type
            </Typography>
            <CoverageTable rows={stats.perType.map(x => ({ label: x.type, total: x.total, filled: x.withText, ac: x.withAC }))} />
          </CardContent>
        </Card>
        <Card variant="outlined" sx={{ flex: 1 }}>
          <CardContent sx={{ py: 1.5 }}>
            <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              Abdeckung pro Team
            </Typography>
            <CoverageTable rows={stats.perTeam.map(x => ({ label: x.team, total: x.total, filled: x.withText, ac: x.withAC }))} />
          </CardContent>
        </Card>
      </Stack>
    </Stack>
  );
}

// ─── Sub-Components ────────────────────────────────────────────────────────

function Kpi({ title, value, subtitle, accent, progress }: {
  title: string; value: string; subtitle?: string; accent?: string; progress?: number;
}) {
  return (
    <Card variant="outlined" sx={{ minWidth: 200, flex: 1 }}>
      <CardContent sx={{ py: 1.5 }}>
        <Typography variant="overline" color="text.secondary" sx={{ display: 'block' }}>
          {title}
        </Typography>
        <Typography variant="h4" sx={{ fontWeight: 700, color: accent ?? 'text.primary', lineHeight: 1.1 }}>
          {value}
        </Typography>
        {subtitle && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            {subtitle}
          </Typography>
        )}
        {progress != null && (
          <LinearProgress
            variant="determinate"
            value={Math.min(100, Math.max(0, progress))}
            sx={{
              mt: 0.75,
              height: 6,
              borderRadius: 3,
              bgcolor: 'action.hover',
              '& .MuiLinearProgress-bar': { bgcolor: accent ?? 'primary.main' },
            }}
          />
        )}
      </CardContent>
    </Card>
  );
}

function CoverageTable({ rows }: {
  rows: { label: string; total: number; filled: number; ac: number }[];
}) {
  if (!rows.length) {
    return <Typography variant="body2" color="text.secondary">Keine Daten.</Typography>;
  }
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr auto auto auto', gap: '2px 12px', alignItems: 'center' }}>
      <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>Name</Typography>
      <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textAlign: 'right' }}>Σ</Typography>
      <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textAlign: 'right' }}>Text %</Typography>
      <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textAlign: 'right' }}>AC %</Typography>
      {rows.slice(0, 20).map(r => {
        const covPct = r.total > 0 ? (r.filled / r.total) * 100 : 0;
        const acPct  = r.total > 0 ? (r.ac     / r.total) * 100 : 0;
        return (
          <Fragment key={r.label}>
            <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {r.label}
            </Typography>
            <Typography variant="body2" sx={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
              {fmtInt(r.total)}
            </Typography>
            <Typography variant="body2" sx={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 700, color: covPct >= 60 ? '#2e7d32' : covPct >= 40 ? '#ef6c00' : '#c62828' }}>
              {covPct.toFixed(0)}%
            </Typography>
            <Typography variant="body2" sx={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 700, color: acPct >= 60 ? '#2e7d32' : acPct >= 40 ? '#ef6c00' : '#c62828' }}>
              {acPct.toFixed(0)}%
            </Typography>
          </Fragment>
        );
      })}
    </Box>
  );
}
